import * as z from "zod";
import { generateMockFromSchema } from "./generate-mock-from-schema";
import { getMockRestResponse, isMockModeEnabled } from "./mock-storage";

export interface MockRestEndpoint {
  /**
   * URL path suffix to match against.
   *
   * Static patterns match exactly, and parameterized patterns can use
   * `:paramName` segments, e.g. "/user/:userId".
   */
  pattern: string;
  /** HTTP method to match. Defaults to "GET" */
  method?: string;
  /** Zod schema for generating mock response (used if data is not provided) */
  schema?: z.ZodTypeAny;
  /** Static data to return. Takes precedence over schema. */
  data?: unknown;
  /** Dynamic response factory. Takes precedence over static data and schema. */
  response?: MockRestResponseFactory;
  /** Optional validators for `:paramName` path parameters. */
  paramValidators?: Record<string, (value: string) => boolean>;
}

export interface MockRestResponseFactoryContext {
  method: string;
  params: Record<string, string>;
  url: string;
}

export type MockRestResponseFactory = (
  context: MockRestResponseFactoryContext,
) => unknown;

export interface MockRestEndpointMatch {
  endpoint: MockRestEndpoint;
  params: Record<string, string>;
  url: string;
}

const registry: MockRestEndpoint[] = [];
let interceptorInstalled = false;

export function registerMockRestEndpoint(endpoint: MockRestEndpoint): void {
  registry.push({ method: "GET", ...endpoint });
}

export function registerMockRestEndpoints(endpoints: MockRestEndpoint[]): void {
  for (const endpoint of endpoints) {
    registerMockRestEndpoint(endpoint);
  }
}

export function findMockEndpoint(
  url: string,
  method: string,
): MockRestEndpointMatch | undefined {
  const upperMethod = method.toUpperCase();
  const pathname = pathnameForMatching(url);
  const matchingMethod = registry.filter((entry) => {
    return entry.method?.toUpperCase() === upperMethod;
  });

  for (const endpoint of matchingMethod) {
    if (
      !hasPathParams(endpoint.pattern) &&
      staticPatternMatches(pathname, endpoint.pattern)
    ) {
      return { endpoint, params: {}, url };
    }
  }

  for (const endpoint of matchingMethod) {
    const params = matchParameterizedPattern(pathname, endpoint);
    if (params) {
      return { endpoint, params, url };
    }
  }

  return undefined;
}

function pathnameForMatching(url: string): string {
  const withoutQuery = url.includes("?") ? url.slice(0, url.indexOf("?")) : url;
  try {
    return new URL(withoutQuery).pathname;
  } catch {
    return withoutQuery.startsWith("/") ? withoutQuery : `/${withoutQuery}`;
  }
}

function trimTrailingSlash(value: string): string {
  if (value.length > 1 && value.endsWith("/")) {
    return value.slice(0, -1);
  }
  return value;
}

function pathSegments(value: string): string[] {
  return trimTrailingSlash(value).split("/").filter(Boolean);
}

function staticPatternMatches(pathname: string, pattern: string): boolean {
  const normalizedPathname = trimTrailingSlash(pathname);
  const normalizedPattern = trimTrailingSlash(pattern);
  return (
    normalizedPathname.endsWith(normalizedPattern) ||
    normalizedPathname.endsWith(`${normalizedPattern}/`)
  );
}

function hasPathParams(pattern: string): boolean {
  return pathSegments(pattern).some((segment) => segment.startsWith(":"));
}

function matchParameterizedPattern(
  pathname: string,
  endpoint: MockRestEndpoint,
): Record<string, string> | null {
  if (!hasPathParams(endpoint.pattern)) {
    return null;
  }

  const patternSegments = pathSegments(endpoint.pattern);
  const pathnameSegments = pathSegments(pathname);
  if (pathnameSegments.length < patternSegments.length) {
    return null;
  }

  const params: Record<string, string> = {};
  const pathOffset = pathnameSegments.length - patternSegments.length;

  for (const [i, patternSegment_] of patternSegments.entries()) {
    const patternSegment = patternSegment_!;
    const pathnameSegment = pathnameSegments[pathOffset + i]!;

    if (patternSegment.startsWith(":")) {
      const paramName = patternSegment.slice(1);
      const value = decodeURIComponent(pathnameSegment);
      const validator = endpoint.paramValidators?.[paramName];
      if (validator && !validator(value)) {
        return null;
      }
      params[paramName] = value;
      continue;
    }

    if (patternSegment !== pathnameSegment) {
      return null;
    }
  }

  return params;
}

function resolveEndpointOverride(
  endpoint: MockRestEndpoint,
): unknown | undefined {
  const method = endpoint.method?.toUpperCase() ?? "GET";
  return (
    getMockRestResponse(`${method} ${endpoint.pattern}`) ??
    getMockRestResponse(endpoint.pattern)
  );
}

function resolveMatchData(match: MockRestEndpointMatch): unknown {
  return resolveMockRestData(match.endpoint, match);
}

function resolveMockRestData(
  endpoint: MockRestEndpoint,
  match?: MockRestEndpointMatch,
): unknown {
  const override = resolveEndpointOverride(endpoint);
  if (override !== undefined) {
    return override;
  }
  if (endpoint.response) {
    return endpoint.response({
      method: endpoint.method?.toUpperCase() ?? "GET",
      params: match?.params ?? {},
      url: match?.url ?? endpoint.pattern,
    });
  }
  if (endpoint.data !== undefined) {
    return endpoint.data;
  }
  if (endpoint.schema) {
    return generateMockFromSchema(endpoint.schema, `rest:${endpoint.pattern}`);
  }
  return {};
}

export function isMongoObjectId(value: string): boolean {
  return /^[a-fA-F0-9]{24}$/.test(value);
}

function pathWithoutQuery(url: string): string {
  return url.includes("?") ? url.slice(0, url.indexOf("?")) : url;
}

function pathWithQuery(url: string): string {
  try {
    const parsedUrl = new URL(url);
    return `${parsedUrl.pathname}${parsedUrl.search}`;
  } catch {
    return url.startsWith("/") ? url : `/${url}`;
  }
}

function pathOnly(url: string): string {
  const fullPath = pathWithoutQuery(url);
  try {
    return new URL(fullPath).pathname;
  } catch {
    return fullPath;
  }
}

function getRestDataOverride(url: string, method: string): unknown | undefined {
  const fullPath = pathWithoutQuery(url);
  const fullPathWithQuery = pathWithQuery(url);
  const path = pathOnly(url);
  const upperMethod = method.toUpperCase();
  return (
    getMockRestResponse(`${upperMethod} ${fullPathWithQuery}`) ??
    getMockRestResponse(fullPathWithQuery) ??
    getMockRestResponse(fullPath) ??
    getMockRestResponse(path) ??
    getMockRestResponse(`${upperMethod} ${path}`)
  );
}

export function installMockRestInterceptor(): void {
  if (interceptorInstalled || typeof window === "undefined") {
    return;
  }
  interceptorInstalled = true;

  // Intercept fetch (used by Effect API HTTP clients)
  const originalFetch = window.fetch;
  window.fetch = async function (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    if (isMockModeEnabled()) {
      const url =
        typeof input === "string"
          ? input
          : input instanceof URL
            ? input.toString()
            : (input as Request).url;
      const method =
        init?.method ?? (input instanceof Request ? input.method : "GET");
      // Check registry first, then fall back to localStorage restData keyed by
      // the matching path suffix (allows fixture restData to work without
      // pre-registering the endpoint).
      const match = findMockEndpoint(url, method);
      if (match) {
        const mockData = resolveMatchData(match);
        console.log(
          `[redoMock] fetch intercepted: ${method} ${url} → mock data`,
        );
        return new Response(JSON.stringify(mockData), {
          status: 200,
          headers: { "Content-Type": "application/json" },
        });
      }
      // Also try localStorage restData directly for endpoints not in registry.
      // Check path suffix so fixture files can use short keys like "/support-tickets"
      // without knowing the full base URL.
      const restDataOverride = getRestDataOverride(url, method);
      if (restDataOverride !== undefined) {
        console.log(
          `[redoMock] fetch intercepted via restData: ${method} ${url} → mock data`,
        );
        return new Response(JSON.stringify(restDataOverride), {
          status: 200,
          headers: { "Content-Type": "application/json" },
        });
      }
    }
    return originalFetch.call(this, input, init);
  };

  const OriginalXHR = window.XMLHttpRequest;
  const originalOpen = OriginalXHR.prototype.open;
  const originalSend = OriginalXHR.prototype.send;

  OriginalXHR.prototype.open = function (
    method: string,
    url: string | URL,
    async?: boolean,
    username?: string | null,
    password?: string | null,
  ) {
    (this as any).__mockMethod = method;
    (this as any).__mockUrl = String(url);
    return originalOpen.call(
      this,
      method,
      url,
      async ?? true,
      username,
      password,
    );
  };

  OriginalXHR.prototype.send = function (
    body?: Document | XMLHttpRequestBodyInit | null,
  ) {
    if (!isMockModeEnabled()) {
      return originalSend.call(this, body);
    }

    const url: string = (this as any).__mockUrl ?? "";
    const method: string = (this as any).__mockMethod ?? "GET";
    const match = findMockEndpoint(url, method);

    let mockData: unknown;
    if (match) {
      mockData = resolveMatchData(match);
    } else {
      // Mirror the fetch interceptor's restData fallback for endpoints that
      // aren't registered but are mocked via a fixture's `restData` block —
      // otherwise axios-style XHR requests (e.g. the orders table) bypass
      // the mock entirely and try to hit a backend that isn't there.
      const restDataOverride = getRestDataOverride(url, method);
      if (restDataOverride === undefined) {
        return originalSend.call(this, body);
      }
      mockData = restDataOverride;
    }

    const responseText = JSON.stringify(mockData);

    console.log(`[redoMock] REST intercepted: ${method} ${url} → mock data`);

    // Simulate async response
    setTimeout(() => {
      Object.defineProperty(this, "readyState", { writable: true, value: 4 });
      Object.defineProperty(this, "status", { writable: true, value: 200 });
      Object.defineProperty(this, "statusText", {
        writable: true,
        value: "OK",
      });
      Object.defineProperty(this, "responseText", {
        writable: true,
        value: responseText,
      });
      Object.defineProperty(this, "response", {
        writable: true,
        value: responseText,
      });
      this.dispatchEvent(new Event("readystatechange"));
      this.dispatchEvent(new Event("load"));
      this.dispatchEvent(new Event("loadend"));
    }, 0);
  };
}

export function getMockRestRegistry(): readonly MockRestEndpoint[] {
  return registry;
}

export async function resolveMockForRestEndpoint(
  pattern: string,
): Promise<unknown> {
  const endpoint = registry.find((e) => e.pattern === pattern);
  if (!endpoint) {
    return {};
  }
  return resolveMockRestData(endpoint);
}

export async function resolveMockForRestRequest(
  url: string,
  method = "GET",
): Promise<unknown | undefined> {
  const match = findMockEndpoint(url, method);
  if (!match) {
    return undefined;
  }
  return resolveMatchData(match);
}
